import VectorSource from 'ol/source/Vector';
import { calculateLatLonOffsetFromMeters, DMMLonLatToDecimalLonLat, rotatePoint } from '../helpers';
import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import { fleetStyle, lineLabelStyle, lineStyle } from './styles';
import { Coordinate } from 'ol/coordinate';
import { Polygon } from 'ol/geom';
import { FleetAnchorLineDrawingPropertyDto } from 'models/mooring';
import { mapFeature, MapRenderOptions } from '../renderOptions';

type RenderAnchorLinesProps = {
  fleetAnchorLines: FleetAnchorLineDrawingPropertyDto[];
  vectorSource: VectorSource;
  fleetCenter: Coordinate;
  rotation: number;
};

type LineArgs = {
  start: Coordinate;
  end: Coordinate;
  isAccident: boolean;
};
function createLine({ end, start, isAccident }: LineArgs) {
  const feature = new Feature({
    id: Math.random(),
    geometry: new LineString([fromLonLat(start), fromLonLat(end)]),
  });

  feature.setStyle(lineStyle(isAccident));
  return feature;
}

export const renderFleet = (
  { fleetAnchorLines, fleetCenter, vectorSource, rotation }: RenderAnchorLinesProps,
  renderOptions: MapRenderOptions
) => {
  const corners = getFleetCorners(fleetCenter, 20).map((corner) => rotatePoint(corner, fleetCenter, rotation));

  const fleetFeature = new Feature({
    geometry: new Polygon([corners.map((x) => fromLonLat(x))]),
  });

  fleetFeature.setStyle(fleetStyle('F', { hover: false }));
  vectorSource.addFeature(fleetFeature);

  for (const li of fleetAnchorLines) {
    const start = corners[li.CornerNo - 1];
    if (start !== undefined && li.AnchorLat && li.AnchorLong) {
      const end = DMMLonLatToDecimalLonLat(li.AnchorLong, li.AnchorLat);
      vectorSource.addFeature(
        createLine({
          start,
          end,
          isAccident: li.IsAccident,
        })
      );

      const labelFeature = new Feature({
        geometry: new Point(fromLonLat(end)),
        type: mapFeature.fleetAnchorLine,
        id: li.Id,
      });

      const label = li.AnalysisNo ?? '';

      labelFeature.setStyle(lineLabelStyle(label));
      vectorSource.addFeature(labelFeature);
    }
  }

  //
};

/**
 * Calculates the coordinates of the corners of a square (fleet)
 * based on a center point and size, with correction for the curvature of the Earth.
 *
 * @param center - The central coordinate of the fleet [longitude, latitude].
 * @param size - The size of the fleet in meters.
 * @returns An array of coordinates representing the four corners of the fleet.
 */
const getFleetCorners = (center: Coordinate, size: number) => {
  const [deltaLon, deltaLat] = calculateLatLonOffsetFromMeters(size, center[1]);
  return [
    [center[0] - deltaLon, center[1] + deltaLat], // Top-left
    [center[0] + deltaLon, center[1] + deltaLat], // Top-right
    [center[0] + deltaLon, center[1] - deltaLat], // Bottom-right
    [center[0] - deltaLon, center[1] - deltaLat], // Bottom-left
  ] as [number, number][];
};
